import { getCurrentInstance, onScopeDispose, computed, reactive } from 'vue';

// Module-level state - shared across all tooltip instances
const tooltipState = reactive({
  activeTooltips: new Map<string, string | null>(),
  // Track which tooltips are currently being hovered (ordered - last one wins)
  hoveredTooltips: new Map<string, string[]>(),
  // Trigger counter to force reactivity updates
  triggerCounter: 0,
  counter: 0,
});

/**
 * Composable for managing tooltip state with automatic group detection.
 * Uses module-level reactive state to ensure only one tooltip is active per group.
 *
 * @function
 * @name useTooltipGroup
 * @description
 * Automatically creates a tooltip group based on the component's parent UID,
 * ensuring that sibling tooltips share the same group and only one can be active.
 *
 * @param {string} [customGroupId] - Optional custom group ID. If not provided, uses parent component UID
 * @returns {Object} Tooltip management interface
 */
export function useTooltipGroup(customGroupId?: string) {
  const instance = getCurrentInstance();

  // Use custom group ID or derive from parent component UID
  const groupId = customGroupId || `group-${instance?.parent?.uid || 'root'}`;

  // Initialize group state if not exists
  if (!tooltipState.activeTooltips.has(groupId)) {
    tooltipState.activeTooltips.set(groupId, null);
  }
  if (!tooltipState.hoveredTooltips.has(groupId)) {
    tooltipState.hoveredTooltips.set(groupId, []);
  }

  const tooltipId = `tooltip-${++tooltipState.counter}`;

  // Reactive computed for active tooltip ID
  const activeId = computed(() => {
    const _ = tooltipState.triggerCounter;
    return tooltipState.activeTooltips.get(groupId) ?? null;
  });

  // Reactive computed for checking if this tooltip is disabled
  const isDisabled = computed(() => {
    const active = activeId.value;
    return active !== null && active !== tooltipId;
  });

  // Get the tooltip that should currently be active (last hovered one)
  const getPreferredActive = (): string | null => {
    const hoveredList = tooltipState.hoveredTooltips.get(groupId);
    if (hoveredList && hoveredList.length > 0) {
      // Return the last hovered tooltip (most recent)
      return hoveredList[hoveredList.length - 1];
    }
    return null;
  };

  const setActive = () => {
    // Only set active if this is the preferred (last hovered) tooltip
    const preferred = getPreferredActive();
    if (preferred === tooltipId || preferred === null) {
      tooltipState.activeTooltips.set(groupId, tooltipId);
      tooltipState.triggerCounter++;
    }
  };

  const setInactive = () => {
    if (tooltipState.activeTooltips.get(groupId) === tooltipId) {
      // Check if another tooltip should become active
      const preferred = getPreferredActive();
      if (preferred && preferred !== tooltipId) {
        tooltipState.activeTooltips.set(groupId, preferred);
      } else {
        tooltipState.activeTooltips.set(groupId, null);
      }
      tooltipState.triggerCounter++;
    }
  };

  const setHovered = (isHovered: boolean) => {
    const hoveredList = tooltipState.hoveredTooltips.get(groupId);
    if (!hoveredList) return;

    const index = hoveredList.indexOf(tooltipId);

    if (isHovered) {
      if (index === -1) {
        // Add to end of list (most recent)
        hoveredList.push(tooltipId);
      }
      // If this tooltip is now the last hovered, it should become active
      // and close any other open tooltip
      if (hoveredList[hoveredList.length - 1] === tooltipId) {
        const currentActive = tooltipState.activeTooltips.get(groupId);
        if (currentActive !== tooltipId) {
          tooltipState.activeTooltips.set(groupId, tooltipId);
          tooltipState.triggerCounter++;
        }
      }
    } else {
      if (index !== -1) {
        hoveredList.splice(index, 1);
      }
    }
  };

  // Check if this tooltip should open
  const shouldOpen = computed(() => {
    const _ = tooltipState.triggerCounter;
    return tooltipState.activeTooltips.get(groupId) === tooltipId;
  });

  // Cleanup on unmount
  onScopeDispose(() => {
    const hoveredList = tooltipState.hoveredTooltips.get(groupId);
    if (hoveredList) {
      const index = hoveredList.indexOf(tooltipId);
      if (index !== -1) {
        hoveredList.splice(index, 1);
      }
    }
    if (tooltipState.activeTooltips.get(groupId) === tooltipId) {
      tooltipState.activeTooltips.set(groupId, null);
      tooltipState.triggerCounter++;
    }
  });

  return {
    tooltipId,
    groupId,
    activeId,
    isDisabled,
    shouldOpen,
    setActive,
    setInactive,
    setHovered,
  };
}
